import React, { createContext, useContext, useCallback, useState, useEffect, useRef } from 'react';
import { useWebSocket } from '../hooks/useWebSocket';

const RealTimeContext = createContext(null);

export function RealTimeProvider({ children }) {
  const [lastUpdate, setLastUpdate] = useState(null);
  const [pollingActive, setPollingActive] = useState(true);
  const listenersRef = useRef({});

  // Handle incoming WebSocket messages
  const handleMessage = useCallback((data) => {
    const { type } = data;
    
    // Store the last update
    setLastUpdate(data);

    // Notify all listeners for this event type
    const listeners = listenersRef.current;
    if (listeners[type]) {
      listeners[type].forEach(callback => callback(data));
    }

    // Also notify generic 'all' listeners
    if (listeners['all']) {
      listeners['all'].forEach(callback => callback(data));
    }
  }, []);

  const { isConnected: wsConnected, reconnect } = useWebSocket(handleMessage);
  
  // Consider connected if either WebSocket is connected OR polling is active
  const isConnected = wsConnected || pollingActive;

  // Subscribe to specific event types
  const subscribe = useCallback((eventType, callback) => {
    if (!listenersRef.current[eventType]) {
      listenersRef.current[eventType] = [];
    }
    listenersRef.current[eventType].push(callback);

    // Return unsubscribe function
    return () => {
      listenersRef.current[eventType] = (listenersRef.current[eventType] || []).filter(cb => cb !== callback);
    };
  }, []);

  const value = {
    isConnected,
    lastUpdate,
    subscribe,
    reconnect
  };

  return (
    <RealTimeContext.Provider value={value}>
      {children}
    </RealTimeContext.Provider>
  );
}

export function useRealTime() {
  const context = useContext(RealTimeContext);
  if (!context) {
    throw new Error('useRealTime must be used within a RealTimeProvider');
  }
  return context;
}

// Hook to auto-refresh data when a specific event occurs
export function useRealTimeRefresh(eventTypes, refreshCallback) {
  const { subscribe } = useRealTime();
  const callbackRef = useRef(refreshCallback);
  
  // Update the ref when callback changes
  useEffect(() => {
    callbackRef.current = refreshCallback;
  }, [refreshCallback]);

  useEffect(() => {
    const unsubscribes = [];

    // Ensure eventTypes is an array
    const types = Array.isArray(eventTypes) ? eventTypes : [eventTypes];

    types.forEach(eventType => {
      const unsubscribe = subscribe(eventType, (data) => {
        // Call refresh callback when event occurs
        if (callbackRef.current) {
          callbackRef.current(data);
        }
      });
      unsubscribes.push(unsubscribe);
    });

    return () => {
      unsubscribes.forEach(unsub => unsub());
    };
  }, [eventTypes, subscribe]);
}

export default RealTimeContext;
